import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import type {AiGenerationBoxProps, SplitSection} from './ai-generation-box.types';
import {AiGenerationBoxErrorCode} from './ai-generation-box.types';
import styles from './ai-generation-box.module.scss';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import {Text, TextSize} from '@Components/text';
import {AiGenerationListItem, AiGenerationListItemState} from './components/ai-generation-list-item';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import type {GenerationBatch} from './ai-generation-box.library';
import {createGenerationBatch, getStoredData, saveToSession} from './ai-generation-box.library';
import {useAiWriterRecents} from '@Components/ai-writer/hooks/use-ai-writer-recents';
import {getAssetUrl} from '@Utils/s3.util';
import {Image} from 'react-bootstrap';
import {GenerateIdeasOriginator} from '@Libraries/ai-email-campaign-content-library';

const LOADING_SKELETONS_COUNT = 4;
const VALID_ERROR_CODES = new Set<string>(Object.values(AiGenerationBoxErrorCode).map(String));

export function AiGenerationBox<T>({
  id,
  sessionStorageKey,
  initialConfig,
  fetchRecents,
  onGenerate,
  canGenerate,
  onItemSelect,
  getItemKey,
  options,
  renderItem,
  aiPromptBoxPreviewText,
  showRetryOnError,
  isMobile = false,
  customPopupClasses = '',
}: AiGenerationBoxProps<T>): ReactElement {
  const generationsStorageKey = `${sessionStorageKey}-generations`;
  const storedData = getStoredData<T>(generationsStorageKey);
  const {recents, isRecentsLoading, addRecent} = useAiWriterRecents<T>({fetchRecents, getItemKey});
  const [errorCode, setErrorCode] = useState<AiGenerationBoxErrorCode | null>(null);
  const [generationBatches, setGenerationBatches] = useState<GenerationBatch<T>[]>(storedData?.batches ?? []);
  const [lastPrompt, setLastPrompt] = useState<string>(storedData?.lastPrompt ?? '');
  const previousLastPromptRef = useRef<string>('');

  const {
    actions: promptBoxActions,
    state: promptBoxState,
    selectedContextualMenuItems,
  } = useAiPromptBox(id, {
    ...initialConfig,
    placeholder: aiPromptBoxPreviewText ?? '',
  });

  useEffect(() => {
    return (): void => {
      promptBoxActions.update({prompt: ''});
    };
  }, [promptBoxActions]);

  const parseErrorCode = (error: unknown): AiGenerationBoxErrorCode => {
    const code = error instanceof Object && 'code' in error ? String(error.code) : '';

    return VALID_ERROR_CODES.has(code) ? (code as AiGenerationBoxErrorCode) : AiGenerationBoxErrorCode.UNEXPECTED_ERROR;
  };

  const getErrorText = (): string => {
    switch (errorCode) {
      case AiGenerationBoxErrorCode.DAILY_LIMIT_REACHED:
        return window.i18next.t('pmwjs_daily_limit_reached');
      default:
        return window.i18next.t('pmwjs_unexpected_error');
    }
  };

  const renderRetryButton = (): ReactElement | null => {
    if (!showRetryOnError || errorCode === AiGenerationBoxErrorCode.DAILY_LIMIT_REACHED) {
      return null;
    }

    return (
      <Text
        val={window.i18next.t('pmwjs_retry')}
        size={TextSize.MD}
        className="cursor-pointer"
        underline
        bold
        onClick={() => {
          void handleGenerate();
        }}
      />
    );
  };

  const renderErrorBanner = (): ReactElement | null => {
    if (!errorCode) {
      return null;
    }

    return (
      <div className={`${styles.errorContainer} content-danger spacing-m-b-3`}>
        <Icon icon={'icon-exclamation-triangle'} size={IconSize.SIZE_ICON_16} />
        <Text val={getErrorText()} size={TextSize.MD} />
        {renderRetryButton()}
      </div>
    );
  };

  const renderItemContent = (item: T): ReactElement => {
    if (renderItem) {
      return renderItem(item);
    }

    return <Text val={typeof item === 'string' ? item : ''} size={TextSize.MD} className="content-body" />;
  };

  const renderSkeletons = (skeletonState: AiGenerationListItemState): ReactElement => {
    const skeletons = Array.from({length: LOADING_SKELETONS_COUNT}, (_, i) => <AiGenerationListItem key={i} state={skeletonState} />);

    return <>{skeletons}</>;
  };

  const renderEmptyRecentsState = (): ReactElement => {
    return (
      <div className={styles.emptyRecentsContainer}>
        <Image src={getAssetUrl('help-me-write.webp')} className={styles.emptyRecentsImage} />
        <Text val={window.i18next.t('pmwjs_turn_ideas_into_content')} size={TextSize.MD} />
      </div>
    );
  };

  const handleItemSelect = (item: T, prompt: string): void => {
    addRecent(item);
    onItemSelect(item, prompt);
  };

  const renderItems = (items: T[], keyPrefix: string, prompt: string): ReactElement => {
    return (
      <>
        {items.map((item, index) => (
          <AiGenerationListItem
            key={`${keyPrefix}-${index}`}
            content={renderItemContent(item)}
            onClick={() => {
              handleItemSelect(item, prompt);
            }}
          />
        ))}
      </>
    );
  };

  const renderSubheadingText = (text: string, className = ''): ReactElement => {
    return <Text val={text} size={TextSize.MD} bold className={`content-sub-text spacing-m-b-3 ${styles.subheading} ${className}`} />;
  };

  const isGenerating = (): boolean => {
    return promptBoxState.promptBoxState === PromptBoxState.LOADING || promptBoxState.promptBoxState === PromptBoxState.STOPPABLE_LOADING;
  };

  const renderBatchContent = (prompt: string, body: ReactElement): ReactElement => {
    return (
      <>
        {renderSubheadingText(prompt)}
        <div className={styles.recentsList}>{body}</div>
      </>
    );
  };

  const buildGeneratingSection = (): SplitSection | null => {
    if (!isGenerating()) {
      return null;
    }

    return {
      key: 'generating',
      content: renderBatchContent(promptBoxState.prompt, renderSkeletons(AiGenerationListItemState.GENERATING)),
    };
  };

  const buildBatchSection = (batch: GenerationBatch<T>): SplitSection => {
    return {
      key: `batch-${batch.id}`,
      content: renderBatchContent(batch.prompt, renderItems(batch.items, `batch-${batch.id}`, batch.prompt)),
    };
  };

  const buildRecentlyUsedSection = (): SplitSection | null => {
    if (!isRecentsLoading && recents.length === 0) {
      return null;
    }

    const body = isRecentsLoading ? renderSkeletons(AiGenerationListItemState.LOADING) : renderItems(recents, 'recents', '');

    return {
      key: 'recents',
      content: renderBatchContent(window.i18next.t('pmwjs_recently_used'), body),
    };
  };

  const collectSplitSections = (): SplitSection[] => {
    const sections: SplitSection[] = [];
    const generatingSection = buildGeneratingSection();

    if (generatingSection) {
      sections.push(generatingSection);
    }

    generationBatches.forEach((batch) => {
      sections.push(buildBatchSection(batch));
    });

    const recentsSection = buildRecentlyUsedSection();

    if (recentsSection) {
      sections.push(recentsSection);
    }

    return sections;
  };

  const renderSplitSections = (sections: SplitSection[]): ReactElement => {
    if (sections.length === 0) {
      return renderEmptyRecentsState();
    }

    return (
      <>
        {sections.map((section, index) => (
          <div key={section.key} className={index === 0 ? '' : 'spacing-m-t-4'}>
            {section.content}
          </div>
        ))}
      </>
    );
  };

  const renderGenerationListPanel = (sections: SplitSection[]): ReactElement => {
    return (
      <div className={styles.listPanel}>
        {renderErrorBanner()}
        {renderSplitSections(sections)}
      </div>
    );
  };

  const handleGenerate = async (): Promise<void> => {
    if (canGenerate && !canGenerate()) {
      return;
    }

    previousLastPromptRef.current = lastPrompt;
    setLastPrompt(promptBoxState.prompt);
    promptBoxActions.update({promptBoxState: PromptBoxState.LOADING});
    setErrorCode(null);

    const originator = promptBoxState.prompt === lastPrompt ? GenerateIdeasOriginator.Alter : GenerateIdeasOriginator.Generate;

    try {
      const result = await onGenerate(promptBoxState.prompt, selectedContextualMenuItems, originator);
      const newBatch = createGenerationBatch(promptBoxState.prompt, result);
      const updatedBatches = [newBatch, ...generationBatches];
      setGenerationBatches(updatedBatches);
      saveToSession(generationsStorageKey, {batches: updatedBatches, lastPrompt: promptBoxState.prompt});
    } catch (error: unknown) {
      setErrorCode(parseErrorCode(error));
      setLastPrompt(previousLastPromptRef.current);
    } finally {
      promptBoxActions.update({promptBoxState: PromptBoxState.IDLE});
    }
  };

  const splitSections = collectSplitSections();
  const hasListPanelContent = splitSections.length > 0 || errorCode !== null;

  return (
    <div className={`${styles.popupContainer} ${isMobile ? styles.popupContainerMobile : ''} ${customPopupClasses}`}>
      <div className={`${styles.scrollArea} ${hasListPanelContent ? '' : styles.scrollAreaEmpty}`}>
        <AiPromptBox
          containerClasses={`_full-width ${styles.promptBox}`}
          id={id}
          onGenerate={handleGenerate}
          initialConfig={initialConfig}
          onRegenerate={handleGenerate}
          options={options}
          inputClasses={styles.inputClasses}
          inputTestId={PosterEditorTestId.AI_WRITER_PROMPT_INPUT}
          submitTestId={PosterEditorTestId.AI_WRITER_PROMPT_SUBMIT}
        />
        {renderGenerationListPanel(splitSections)}
      </div>
    </div>
  );
}
